import { BottomSheet, LoadingSpinner, type UseModalState } from "@clipboard-health/ui-components";
import { Box } from "@mui/material";
import { useGetExternalAccounts } from "@src/appV2/redesign/Accounts/PayoutSettings/api/useGetExternalAccounts";
import { useGetPayoutSettings } from "@src/appV2/redesign/Accounts/PayoutSettings/api/useGetPayoutSettings";
import { useUpdatePayoutSettings } from "@src/appV2/redesign/Accounts/PayoutSettings/api/useUpdatePayoutSettings";
import {
  type PayoutSettingsChangeableSettings,
  PayoutSettingsPageContent,
} from "@src/appV2/redesign/Accounts/PayoutSettings/components/PayoutSettingsPageContent";
import { useGoToStripeExpressSignIn } from "@src/appV2/redesign/Accounts/PayoutSettings/hooks/useGoToStripeExpressSignIn";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { useCallback, useMemo, useRef } from "react";

export interface StripeOnboardingPayoutSettingsBottomSheetProps {
  modalState: UseModalState;
}

export function StripeOnboardingPayoutSettingsBottomSheet(
  props: StripeOnboardingPayoutSettingsBottomSheetProps
) {
  const { modalState } = props;

  const { data: payoutSettings, isLoading: isPayoutSettingsLoading } = useGetPayoutSettings();
  const { data: externalAccountsResponse, isLoading: isExternalAccountsLoading } =
    useGetExternalAccounts();
  const { mutateAsync: updatePayoutSettings, isLoading: isSaveLoading } = useUpdatePayoutSettings({
    includeExternalAccount: true,
  });
  const { goToStripeExpressSignIn, isGoToStripeExpressSignInLoading } =
    useGoToStripeExpressSignIn();

  const draftSettings = useRef<PayoutSettingsChangeableSettings | undefined>(undefined);

  const externalAccounts = useMemo(
    () => externalAccountsResponse?.data.map((x) => x.attributes) ?? [],
    [externalAccountsResponse]
  );

  const isLoading = isPayoutSettingsLoading || isExternalAccountsLoading;

  const onDraftSettingsUpdated = useCallback(
    (newDraftSettings: PayoutSettingsChangeableSettings | undefined) => {
      draftSettings.current = newDraftSettings;
    },
    []
  );

  const onSave = useCallback(async () => {
    if (draftSettings.current) {
      await updatePayoutSettings({
        payoutSchedule: draftSettings.current.selectedPayoutSchedule,
        preferredExternalAccountStripeId:
          draftSettings.current.selectedPreferredExternalAccountStripeId,
      });
      draftSettings.current = undefined;
      modalState.closeModal();
    }
  }, [updatePayoutSettings, modalState]);

  return (
    <BottomSheet
      modalState={modalState}
      header={<BottomSheetHeader onClose={modalState.closeModal} />}
    >
      {isLoading && (
        <Box sx={{ display: "flex", flex: 1, alignItems: "center", justifyContent: "center" }}>
          <LoadingSpinner size="large" />
        </Box>
      )}
      {!isLoading && payoutSettings && payoutSettings.meta.payoutCapability !== "none" && (
        <PayoutSettingsPageContent
          payoutSchedule={payoutSettings.attributes.payoutSchedule}
          preferredExternalAccountStripeId={
            payoutSettings.attributes.preferredExternalAccountStripeId
          }
          instantPayFee={payoutSettings.meta.instantPayoutFee}
          payoutCapability={payoutSettings.meta.payoutCapability}
          externalAccounts={externalAccounts}
          isSaveLoading={isSaveLoading}
          isGoToStripeLoading={isGoToStripeExpressSignInLoading}
          onDraftSettingsUpdated={onDraftSettingsUpdated}
          onSave={onSave}
          onGoToStripe={goToStripeExpressSignIn}
        />
      )}
    </BottomSheet>
  );
}
